<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { XMarkIcon } from '@heroicons/vue/24/outline'

const props = defineProps({
    title: {
        type: String,
        default: '',
    },
    showClose: {
        type: Boolean,
        default: true,
    },
    contentClass: {
        type: String,
        default: '',
    },
    headerClass: {
        type: String,
        default: '',
    },
    bodyClass: {
        type: String,
        default: '',
    },
    footerClass: {
        type: String,
        default: '',
    },
    teleportTo: {
        type: String,
        default: 'body',
    },
    ariaLabel: {
        type: String,
        default: '',
    },
    closeOnBackdrop: {
        type: Boolean,
        default: true,
    },
    closeOnEscape: {
        type: Boolean,
        default: true,
    },
})

const emit = defineEmits(['close'])

const modalContentRef = ref(null)
const previouslyFocused = ref(null)

const FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(', ')

function getFocusable() {
    return modalContentRef.value
        ? Array.from(modalContentRef.value.querySelectorAll(FOCUSABLE))
        : []
}

function trapFocus(e) {
    if (e.key !== 'Tab') return
    const focusable = getFocusable()
    if (!focusable.length) return
    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    if (e.shiftKey) {
        if (document.activeElement === first) {
            e.preventDefault()
            last.focus()
        }
    } else {
        if (document.activeElement === last) {
            e.preventDefault()
            first.focus()
        }
    }
}

function handleEscape(e) {
    if (e.key === 'Escape' && props.closeOnEscape) {
        emit('close')
    }
}

onMounted(() => {
    previouslyFocused.value = document.activeElement
    document.addEventListener('keydown', trapFocus)
    document.addEventListener('keydown', handleEscape)
    // focus first focusable element
    const focusable = getFocusable()
    if (focusable.length) focusable[0].focus()
})

onBeforeUnmount(() => {
    document.removeEventListener('keydown', trapFocus)
    document.removeEventListener('keydown', handleEscape)
    if (previouslyFocused.value) previouslyFocused.value.focus()
})
</script>

<template>
    <Teleport :to="teleportTo">
        <div class="modal-overlay" @click.self="closeOnBackdrop && $emit('close')">
            <div
                ref="modalContentRef"
                class="modal-content"
                :class="contentClass"
                role="dialog"
                aria-modal="true"
                :aria-label="ariaLabel || title || undefined"
            >
                <div class="modal-header" :class="headerClass">
                    <slot name="header">
                        <h2 class="modal-header-title">{{ title }}</h2>
                        <button v-if="showClose" class="modal-close" type="button" aria-label="Sluiten" @click="$emit('close')">
                            <XMarkIcon class="w-6 h-6" />
                        </button>
                    </slot>
                </div>
                <div class="modal-body" :class="bodyClass">
                    <slot />
                </div>
                <div v-if="$slots.footer" class="modal-footer" :class="footerClass">
                    <slot name="footer" />
                </div>
            </div>
        </div>
    </Teleport>
</template>
